'use client';

import { Link, useNavigate } from 'react-router-dom';
import { BookOpen, Star } from 'lucide-react';
import { applyFallbackImageOnError, FALLBACK_IMAGE_URL, getObraImageUrl } from '@/utils/image-utils';
import { generateWorkLink, generateChapterLink } from '@/utils/utils';
import { accentHexForFormato, rgbaFromHex } from '@/constants/obras-generos';

export type AtualizadoObraCardObra = {
    id: number;
    nome?: string;
    imagem?: string;
    capitulos?: { id?: number; numero?: number; nome?: string }[];
    total_capitulos?: number;
};

function getChapterDate(cap: unknown): string | null {
    if (!cap || typeof cap !== 'object') return null;
    const c = cap as Record<string, unknown>;
    return (c.lancado_em ??
        c.data_criacao ??
        c.criado_em ??
        c.cap_lancado_em ??
        c.cap_criado_em ??
        c.cap_data_criacao) as string | null;
}

function isNewChapter(cap: unknown): boolean {
    if (!cap) return false;
    const dateVal = getChapterDate(cap);
    if (!dateVal) return false;
    const capDate = new Date(dateVal);
    const now = new Date();
    const daysDiff = Math.floor((now.getTime() - capDate.getTime()) / (24 * 60 * 60 * 1000));
    return daysDiff <= 5 && daysDiff >= 0;
}

function formatRelativeCapAgo(dateStr: string): string {
    try {
        const d = new Date(dateStr);
        if (Number.isNaN(d.getTime())) return '—';
        const diff = Date.now() - d.getTime();
        if (diff < 0) return '—';
        const sec = Math.floor(diff / 1000);
        if (sec < 60) return 'agora';
        const min = Math.floor(diff / 60_000);
        if (min < 60) return `${min}min atrás`;
        const h = Math.floor(diff / 3_600_000);
        if (h < 24) return `${h}h atrás`;
        const days = Math.floor(diff / 86_400_000);
        return `${Math.max(1, days)}d atrás`;
    } catch {
        return '—';
    }
}

interface AtualizadoObraCardProps {
    obra: AtualizadoObraCardObra;
    formatoId: number;
    /** Grelhas densas (ex.: 7 colunas na home desktop). */
    compact?: boolean;
    /** `eager` evita lazy-load incorreto em grelhas da home (hover forçava repaint). */
    coverLoading?: 'eager' | 'lazy';
}

export default function AtualizadoObraCard({
    obra,
    formatoId,
    compact = false,
    coverLoading = 'lazy',
}: AtualizadoObraCardProps) {
    const navigate = useNavigate();
    const accent = accentHexForFormato(formatoId);
    const obraHref = generateWorkLink(obra.id);

    const caps = (Array.isArray(obra.capitulos) ? obra.capitulos.slice(0, 2) : []).filter(
        (c): c is { id?: number; numero?: number; nome?: string } => c != null && typeof c === 'object',
    );

    const capTotal =
        typeof obra.total_capitulos === 'number' && obra.total_capitulos > 0
            ? obra.total_capitulos
            : Array.isArray(obra.capitulos)
              ? obra.capitulos.length
              : 0;

    const coverRound = compact ? 'rounded-xl' : 'rounded-2xl';
    const imgSize = compact ? 200 : 280;
    const capMinH = compact ? 'min-h-[2.125rem]' : 'min-h-[2.5rem]';
    const capPad = compact ? 'px-2 py-1.5' : 'px-3 py-2';
    const capText = compact ? 'text-xs' : 'text-sm';
    const capBlockMinH = compact ? 'min-h-[4.75rem]' : 'min-h-[5.75rem]';
    const titleMinH = compact ? 'min-h-[2.75rem]' : 'min-h-[3.125rem]';
    const titleClass = compact
        ? 'line-clamp-2 text-sm font-semibold leading-snug text-white transition-colors group-hover:[color:var(--obra-accent)]'
        : 'line-clamp-2 text-[1.03rem] font-semibold leading-snug text-white transition-colors group-hover:[color:var(--obra-accent)]';

    return (
        <div className="group flex h-full w-full min-w-0 flex-col">
            <Link
                to={obraHref}
                className={`group/cover relative z-0 block aspect-[2/3] w-full shrink-0 overflow-hidden border border-white/10 bg-white/[0.03] transition-[transform,box-shadow,border-color] duration-200 ease-out will-change-transform hover:z-20 hover:-translate-y-2 hover:border-white/25 hover:shadow-[0_18px_44px_rgba(0,0,0,0.5)] motion-safe:hover:scale-[1.03] motion-reduce:hover:translate-y-0 motion-reduce:hover:scale-100 motion-reduce:hover:shadow-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 active:translate-y-0 active:scale-[0.99] active:shadow-none ${coverRound}`}
                style={{ outlineColor: accent }}
            >
                <img
                    src={getObraImageUrl(obra.id, obra.imagem, imgSize) || FALLBACK_IMAGE_URL}
                    alt=""
                    onError={(e) => applyFallbackImageOnError(e.currentTarget)}
                    loading={coverLoading}
                    decoding="async"
                    {...(coverLoading === 'eager' ? {} : { fetchPriority: 'low' as const })}
                    className="h-full w-full object-cover transition-transform duration-200 ease-out motion-reduce:transition-none motion-reduce:group-hover/cover:scale-100 group-hover/cover:scale-105"
                />
                {capTotal > 0 && (
                    <span
                        className={`pointer-events-none absolute z-[1] inline-flex items-center rounded-full border border-white/15 bg-black/25 shadow-[0_2px_14px_rgba(0,0,0,0.45)] backdrop-blur-2xl antialiased ${
                            compact
                                ? 'left-1.5 top-1.5 gap-1 px-1.5 py-0.5'
                                : 'left-2 top-2 gap-1.5 px-2.5 py-1'
                        }`}
                        title={`${capTotal} capítulos`}
                    >
                        <BookOpen
                            size={compact ? 10 : 12}
                            strokeWidth={2.5}
                            className="shrink-0 text-white"
                            aria-hidden
                        />
                        <span
                            className={`tabular-nums leading-none text-white ${compact ? 'text-[10px]' : 'text-[11px]'}`}
                            style={{ fontWeight: 800 }}
                        >
                            {capTotal}
                        </span>
                        {!compact && (
                            <span className="text-[11px] leading-none tracking-wide text-white" style={{ fontWeight: 800 }}>
                                Capítulos
                            </span>
                        )}
                    </span>
                )}
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-transparent opacity-0 transition-opacity group-hover/cover:opacity-100" />
            </Link>
            <div className={`flex min-h-0 flex-1 flex-col px-0.5 ${compact ? 'pt-1.5' : 'pt-2'}`}>
                <div className={`${titleMinH} shrink-0`} style={{ ['--obra-accent' as string]: accent }}>
                    <Link
                        to={obraHref}
                        className="block no-underline focus-visible:rounded-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                        style={{ outlineColor: accent }}
                    >
                        <h3 className={titleClass}>{obra.nome}</h3>
                    </Link>
                </div>
                <div
                    className={`flex flex-col ${capBlockMinH} ${compact ? 'mt-1.5 gap-1' : 'mt-2 gap-1.5'}`}
                >
                    {caps.map((cap) => {
                        const capId = cap?.id;
                        const isNovo = isNewChapter(cap);
                        const dateStr = getChapterDate(cap);
                        const label = cap?.numero !== undefined ? `Cap. ${cap.numero}` : (cap?.nome ?? 'Cap.');
                        if (!capId) return null;
                        return (
                            <button
                                key={capId}
                                type="button"
                                onClick={() => navigate(generateChapterLink(capId))}
                                style={
                                    isNovo
                                        ? {
                                              backgroundColor: rgbaFromHex(accent, 0.13),
                                              boxShadow: `inset 0 0 0 1px ${rgbaFromHex(accent, 0.34)}`,
                                          }
                                        : undefined
                                }
                                className={`group/cap flex w-full items-center justify-between gap-1.5 rounded-lg transition-colors ${capMinH} ${capPad} ${capText} ${
                                    isNovo ? 'hover:brightness-110 active:brightness-95' : 'bg-white/[0.05] hover:bg-white/[0.09]'
                                }`}
                            >
                                <span
                                    className={`inline-flex min-w-0 flex-1 items-center gap-1 truncate text-left font-bold text-white/93 group-hover/cap:text-white ${capText}`}
                                >
                                    {label}
                                    {isNovo && (
                                        <Star
                                            size={compact ? 11 : 13}
                                            strokeWidth={1.5}
                                            className="shrink-0"
                                            style={{ fill: accent, stroke: accent }}
                                            aria-hidden
                                        />
                                    )}
                                </span>
                                <span
                                    className={`shrink-0 tabular-nums font-bold text-white/40 group-hover/cap:text-white/55 ${compact ? 'text-[10px]' : 'text-xs'}`}
                                >
                                    {dateStr ? formatRelativeCapAgo(dateStr) : '—'}
                                </span>
                            </button>
                        );
                    })}
                    {caps.length < 2 && <div className={`${capMinH} rounded-lg bg-transparent`} aria-hidden />}
                </div>
            </div>
        </div>
    );
}
